import { _, FreezerService, IAjaxState, managedAjaxUtil } from 'Imports';
import { GroupDto, IntegrationPartnerDataApiFactory, TelematicProviderVehicleResponse, UserRolesApiFactory } from '$Generated/api';
import { UserManagementService } from '$State/UserManagementFreezerService';
import { IIntegrationPartnerManager } from 'integrationPartner/integrationPartnerAbstraction';

const InjectedPropName = 'integrationPartnerData';

export interface IIntegrationPartnerDataState {
    hasError: boolean;
    driversResults: IAjaxState<any[]>;
    deviceResults: IAjaxState<TelematicProviderVehicleResponse[]>;
    currentUserResult: IAjaxState<any>;
    userRolesResult: IAjaxState<any[]>;
    updateUserRolesResult: any;
    integrationPartnerManager: any;
    canRender: boolean;
    groupsFilter: string;
}

class IntegrationPartnerDataFreezerService extends FreezerService<IIntegrationPartnerDataState, typeof InjectedPropName> {
    constructor() {
        super(
            {
                hasError: false,
                driversResults: managedAjaxUtil.createInitialState(),
                deviceResults: managedAjaxUtil.createInitialState(),
                currentUserResult: managedAjaxUtil.createInitialState(),
                userRolesResult: managedAjaxUtil.createInitialState(),
                updateUserRolesResult: undefined,
                integrationPartnerManager: undefined,
                canRender: true,
                groupsFilter: '',
            },
            InjectedPropName,
        );
    }

    public async getDrivers(): Promise<void | any[]> {
        return managedAjaxUtil.fetchResults({
            ajaxStateProperty: 'driversResults',
            freezer: this.freezer,
            onExecute: (apiOptions, params) => {
                if (params === undefined) {
                    throw new Error('Parameters is undefined');
                }
                const IntegrationPartnerDataApi = IntegrationPartnerDataApiFactory(apiOptions.wrappedFetch, apiOptions.baseUrl);
                return IntegrationPartnerDataApi.apiV1IntegrationDriversGet(params, apiOptions.fetchOptions);
            },
            params: {
                ...UserManagementService.generateIntegrationUserParams(),
                ...{ groupsFilter: this.getState().groupsFilter },
            },
            onOk: (data: any[]) => {
                return data;
            },
            onError: (err: any) => {
                console.error('error loading drivers', err);
                this.freezer.get().set({ hasError: true });
            },
        });
    }

    public async getDevices(includeCameraPairings: boolean = false): Promise<void | TelematicProviderVehicleResponse[]> {
        return managedAjaxUtil.fetchResults({
            ajaxStateProperty: 'deviceResults',
            freezer: this.freezer,
            onExecute: (apiOptions, params) => {
                if (params === undefined) {
                    throw new Error('Parameters is undefined');
                }
                const IntegrationPartnerDataApi = IntegrationPartnerDataApiFactory(apiOptions.wrappedFetch, apiOptions.baseUrl);
                return IntegrationPartnerDataApi.apiV1IntegrationDevicesGet(params, apiOptions.fetchOptions);
            },
            params: {
                ...UserManagementService.generateIntegrationUserParams(),
                ...{ groupsFilter: this.getState().groupsFilter, includeCameraPairings: includeCameraPairings },
            },
            onOk: (data: TelematicProviderVehicleResponse[]) => {
                return data;
            },
            onError: (err: any) => {
                console.error('error loading devices', err);
                this.freezer.get().set({ hasError: true });
            },
        });
    }

    public async getUser(): Promise<void | any> {
        return managedAjaxUtil.fetchResults({
            ajaxStateProperty: 'currentUserResult',
            freezer: this.freezer,
            onExecute: (apiOptions, params) => {
                if (params === undefined) {
                    throw new Error('Parameters is undefined');
                }
                const IntegrationPartnerDataApi = IntegrationPartnerDataApiFactory(apiOptions.wrappedFetch, apiOptions.baseUrl);
                return IntegrationPartnerDataApi.apiV1IntegrationUserGet(params, apiOptions.fetchOptions);
            },
            params: {
                ...UserManagementService.generateIntegrationUserParams(),
            },
            onOk: (data: any) => {
                if (data !== undefined) {
                    this.freezer.get().set({ hasError: false });
                }
                return data;
            },
            onError: (err: any) => {
                console.error('error loading user', err);
                this.freezer.get().set({ hasError: true });
            },
        });
    }

    public async getUserRoles(): Promise<void | any> {
        return managedAjaxUtil.fetchResults({
            ajaxStateProperty: 'userRolesResult',
            freezer: this.freezer,
            onExecute: (apiOptions, params) => {
                if (params === undefined) {
                    throw new Error('Parameters is undefined');
                }
                const UserRoleApi = UserRolesApiFactory(apiOptions.wrappedFetch, apiOptions.baseUrl);
                return UserRoleApi.apiV1UserRolesGet(params, apiOptions.fetchOptions);
            },
            params: {
                ...UserManagementService.generateIntegrationUserParams(),
            },
            onOk: (data: any) => {
                if (data !== undefined) {
                    this.freezer.get().set({ hasError: false });
                }
                return data;
            },
            onError: (err: any) => {
                console.error('error getting user roles from current user', err);
                this.freezer.get().set({ hasError: true });
            },
        });
    }

    public getUsername(): string {
        const currentUser = this.getState().currentUserResult;
        if (currentUser !== undefined) {
            return currentUser.data?.name;
        }
        return '';
    }

    public getUserIsMetric(): boolean {
        const currentUser = this.getState().currentUserResult;
        if (currentUser !== undefined && currentUser.data) {
            return currentUser.data.isMetric;
        }
        return false;
    }

    public getUserRolesState(): any {
        return this.getState().userRolesResult;
    }

    public getIntegrationPartnerManager(): IIntegrationPartnerManager {
        return this.freezer.get().integrationPartnerManager;
    }

    public setIntegrationPartnerManager(value: IIntegrationPartnerManager) {
        this.freezer.get().set({ integrationPartnerManager: value });
    }

    public getCanRender(): boolean {
        return this.freezer.get().canRender;
    }

    public setCanRender(value: boolean) {
        this.freezer.get().set({ canRender: value });
    }

    public setGroupsFilter(groupsFilter: string) {
        const fullGroupResult = this.flattenGroups(this.getCompanyGroups());
        const groupIds = groupsFilter
            .split(',')
            .map((id) => id.trim())
            .filter((id) => id.length > 0);
        const fullGroupResultIds = fullGroupResult.map((g: GroupDto) => g.id);

        // Only keep IDs that exist in frontendGroupIds (case-sensitive)
        const validGroupIds = groupIds.filter((id) => fullGroupResultIds.includes(id));
        this.freezer.get().set({ groupsFilter: validGroupIds.join(',') });
    }

    public flattenGroups(groups: GroupDto[]): GroupDto[] {
        const flatList: GroupDto[] = [];
        for (const group of groups) {
            flatList.push(group);
            if (group.children && group.children.length > 0) {
                flatList.push(...this.flattenGroups(group.children));
            }
        }
        return flatList;
    }

    public getGroupsFilter(): string {
        return this.getState().groupsFilter ?? '';
    }

    public getCompanyGroups(): GroupDto[] {
        const currentUser = this.getState().currentUserResult;
        if (currentUser !== undefined && currentUser.data) {
            return currentUser.data.companyGroups;
        }
        return [];
    }

    public getCompanyName(): string {
        const groups = this.getCompanyGroups() || [];
        const globalGroup = this.flattenGroups(groups).find((g) => g.isGlobalReportingGroup);
        return globalGroup?.name ?? groups[0]?.name ?? UserManagementService.getCurrentFleetName();
    }
}

export const IntegrationPartnerDataService = new IntegrationPartnerDataFreezerService();
export type IIntegrationPartnerDataInjectedProps = ReturnType<IntegrationPartnerDataFreezerService['getPropsForInjection']>;
